import React, { useState, useEffect } from 'react';
import { useOrg } from '../contexts/OrgContext';
import { CreditCard, Coins, ArrowRight, Check, Info, Users, X } from 'lucide-react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Database } from '../types/database.types';
import ModalOverlay from '../components/ui/ModalOverlay';
import { SkeletonHeader, SkeletonRegion, SkeletonStats, SkeletonTable, Skel } from '../components/ui/PageState';
import { hasActiveStripeSub } from '../lib/billing';
import { avulsosFromLedger } from '../lib/avulsosCredits';
import { cheapestImage, cheapestVideo, planMediaLine, planProductFeatures } from '../lib/planAllowance';

type Plan = Database['public']['Tables']['plans']['Row'];

type PurchaseRow = {
  id: string;
  amount: number;
  type: string;
  description: string | null;
  created_at: string | null;
};

const CHEAPEST_VIDEO = cheapestVideo();
const CHEAPEST_IMAGE = cheapestImage();
const PURCHASE_TYPES = new Set(['purchase', 'subscription_grant', 'subscription_seat', 'plan_renewal']);
const PURCHASE_LABEL: Record<string, string> = {
  purchase: 'Créditos avulsos',
  subscription_grant: 'Créditos do plano',
  subscription_seat: 'Créditos de assento',
  plan_renewal: 'Renovação do plano',
};

type SeatRemovePreview = {
  remove_seats: number;
  extra_seats_before: number;
  extra_seats_after: number;
  members: number;
  seat_cap: number;
  credit_cents: number;
  monthly_seats_before_cents: number;
  monthly_seats_after_cents: number;
  monthly_delta_cents: number;
  remaining_days: number;
  period_end: number;
  credit_note: string;
  credits_note: string;
  credits_requested?: number;
  credits_clawback?: number;
  credits_balance?: number;
  credits_balance_after?: number;
  lines?: { description: string; amount_cents: number }[];
};

function PlanCardsSkeleton() {
  return (
    <div className="plans-grid" style={{ pointerEvents: 'none' }}>
      {[0, 1, 2].map((i) => (
        <div key={i} className="plan-card" style={{ cursor: 'default', gap: '0.75rem' }}>
          <Skel w="46%" h={16} />
          <Skel w="62%" h={28} />
          <Skel w="78%" h={12} />
          <Skel w="64%" h={12} />
          <Skel w="100%" h={44} style={{ marginTop: 'auto', borderRadius: 10 }} />
        </div>
      ))}
    </div>
  );
}

async function invokeManageError(error: { message?: string; context?: Response } | null) {
  if (!error) return null;
  try {
    const body = await error.context?.json();
    if (body?.error) return body.error as string;
  } catch { /* ignore */ }
  return error.message || null;
}

export default function Payments() {
  const { org, role, refreshOrg, loading: orgLoading } = useOrg();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [readyModels, setReadyModels] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [packSeconds, setPackSeconds] = useState(CHEAPEST_VIDEO.seconds);
  const [extraSeats, setExtraSeats] = useState(0);
  const [addSeatsQty, setAddSeatsQty] = useState(1);
  const [addingSeats, setAddingSeats] = useState(false);
  const [removeSeatsQty, setRemoveSeatsQty] = useState(1);
  const [removingSeats, setRemovingSeats] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [removePreview, setRemovePreview] = useState<SeatRemovePreview | null>(null);
  const [canceling, setCanceling] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [avulsos, setAvulsos] = useState<{ spent: number; total: number } | null>(null);
  const [purchases, setPurchases] = useState<PurchaseRow[] | null>(null);
  const [searchParams] = useSearchParams();

  const success = searchParams.get('success') === 'true';
  const canceled = searchParams.get('canceled') === 'true';
  const isEmpresarialCurrent = org?.plans?.name === 'Empresarial';
  const canManageBilling = role === 'admin' || role === 'fin_ops';
  const subStatus = org?.stripe_subscription_status || '';
  const hasStripeSub = hasActiveStripeSub(org);
  const isCanceling = subStatus === 'cancel_at_period_end';
  const currentExtraSeats = org?.extra_seats ?? 0;
  const includedSeats = org?.included_seats ?? 1;
  const seatCap = includedSeats + currentExtraSeats;
  const vacantSeats = Math.max(0, seatCap - memberCount);
  // Só assentos extras que estão vagos (não ocupados por membros)
  const removableVacantSeats = Math.min(currentExtraSeats, vacantSeats);

  const monthlyCredits = Math.max(0, org?.subscription_credits ?? 0);
  const purchasedCredits = Math.max(0, org?.purchased_credits ?? 0);

  useEffect(() => {
    if (!org?.id) return;
    let cancel = false;
    (async () => {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('id, amount, type, description, created_at')
        .eq('org_id', org.id)
        .order('created_at', { ascending: true })
        .limit(10000);
      if (cancel || error || !data) return;
      setAvulsos(avulsosFromLedger(data, Number(org.purchased_credits) || 0));
      setPurchases(
        data
          .filter((row) => PURCHASE_TYPES.has(row.type))
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')),
      );
    })();
    return () => { cancel = true; };
  }, [org?.id, org?.purchased_credits]);
  const monthlyAllotment = Math.max(
    0,
    (org?.plans?.credits_per_month ?? 0) + currentExtraSeats * 500,
  );
  const monthlyBarMax = Math.max(monthlyAllotment, monthlyCredits, 1);
  const monthlyPct = Math.min(100, Math.round((monthlyCredits / monthlyBarMax) * 100));
  const purchasedBarMax = Math.max(avulsos?.total ?? purchasedCredits, 1);
  const purchasedPct = Math.min(100, Math.round(((avulsos?.spent ?? 0) / purchasedBarMax) * 100));

  const fmtCredits = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  const minPackSeconds = CHEAPEST_VIDEO.seconds;
  const seconds = Math.max(minPackSeconds, packSeconds);
  const customCredits = seconds * CHEAPEST_VIDEO.creditsPerSecond;

  const handleBuyCredits = async () => {
    if (!hasStripeSub) return;
    try {
      setBuying(true);
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { credits: customCredits },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de checkout não retornada');
      }
    } catch (err: any) {
      console.error('Erro ao iniciar checkout:', err);
      alert(err.message || 'Não foi possível iniciar o pagamento. Tente novamente mais tarde.');
    } finally {
      setBuying(false);
    }
  };

  const handleAddSeats = async () => {
    try {
      setAddingSeats(true);
      const qty = Math.max(1, addSeatsQty);
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { add_seats: qty },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('URL de checkout não retornada');
    } catch (err: any) {
      console.error('Erro ao adicionar assentos:', err);
      alert(err.message || 'Não foi possível adicionar assentos.');
    } finally {
      setAddingSeats(false);
    }
  };

  const openRemoveSeatsModal = async () => {
    try {
      setPreviewLoading(true);
      const qty = Math.max(1, Math.min(removeSeatsQty, removableVacantSeats));
      if (removableVacantSeats < 1) {
        throw new Error('Não há assentos vagos para remover');
      }
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'preview_remove_seats', remove_seats: qty },
      });
      if (error) {
        throw new Error((await invokeManageError(error)) || 'Não foi possível calcular o crédito.');
      }
      if (data?.error) throw new Error(data.error);
      setRemovePreview(data as SeatRemovePreview);
    } catch (err: any) {
      console.error('Erro ao pré-visualizar remoção:', err);
      alert(err.message || 'Não foi possível abrir a confirmação.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmRemoveSeats = async () => {
    if (!removePreview) return;
    try {
      setRemovingSeats(true);
      const qty = removePreview.remove_seats;
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'remove_seats', remove_seats: qty },
      });
      if (error) {
        throw new Error((await invokeManageError(error)) || 'Não foi possível remover assentos.');
      }
      if (data?.error) throw new Error(data.error);
      setRemovePreview(null);
      await refreshOrg(false);
      alert(data?.message || `${qty} assento(s) vago(s) removido(s).`);
      setRemoveSeatsQty(1);
    } catch (err: any) {
      console.error('Erro ao remover assentos:', err);
      alert(err.message || 'Não foi possível remover assentos.');
    } finally {
      setRemovingSeats(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (
      !window.confirm(
        'Cancelar a assinatura ao fim do período atual? Você mantém o acesso até lá. Créditos já comprados permanecem.',
      )
    ) {
      return;
    }
    try {
      setCanceling(true);
      const { data, error } = await supabase.functions.invoke('manage-subscription', {
        body: { action: 'cancel' },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await refreshOrg(false);
      alert(data?.message || 'Cancelamento agendado.');
    } catch (err: any) {
      console.error('Erro ao cancelar assinatura:', err);
      alert(err.message || 'Não foi possível cancelar a assinatura.');
    } finally {
      setCanceling(false);
    }
  };

  const handleSubscribe = async (planId: string, planName: string) => {
    try {
      setSubscribing(planId);
      const body: { plan_id: string; extra_seats?: number } = { plan_id: planId };
      if (planName === 'Empresarial') {
        body.extra_seats = Math.max(0, extraSeats);
      }
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('URL de checkout não retornada');
      }
    } catch (err: any) {
      console.error('Erro ao iniciar assinatura:', err);
      alert(err.message || 'Não foi possível iniciar a assinatura.');
    } finally {
      setSubscribing(null);
    }
  };

  useEffect(() => {
    async function fetchPlans() {
      const [plansRes, modelsRes] = await Promise.all([
        supabase.from('plans').select('*').order('monthly_price', { ascending: true }),
        supabase.from('video_templates').select('id', { count: 'exact', head: true }).eq('status', 'published'),
      ]);

      if (plansRes.data && !plansRes.error) setPlans(plansRes.data);
      if (!modelsRes.error && typeof modelsRes.count === 'number') setReadyModels(modelsRes.count);
      setLoading(false);
    }
    fetchPlans();
  }, []);

  useEffect(() => {
    refreshOrg(false);
  }, [refreshOrg]);

  useEffect(() => {
    if (!org?.id) {
      setMemberCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      // Same source as UserManagement — direct table count is blocked by RLS
      const { data, error } = await supabase.rpc('get_organization_members', {
        p_org_id: org.id,
      });
      if (!cancelled && !error) {
        setMemberCount((data || []).length);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [org?.id, org?.extra_seats, success]);

  useEffect(() => {
    if (removableVacantSeats < 1) {
      setRemoveSeatsQty(1);
      return;
    }
    setRemoveSeatsQty((q) => Math.min(Math.max(1, q), removableVacantSeats));
  }, [removableVacantSeats]);

  useEffect(() => {
    if (success) {
      refreshOrg(false);
      const t = window.setTimeout(() => refreshOrg(false), 2000);
      return () => window.clearTimeout(t);
    }
  }, [success, refreshOrg]);

  const usd = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

  if (orgLoading) {
    return (
      <div className="page-container">
        <SkeletonRegion label="Carregando pagamentos…">
          <SkeletonHeader />
          <div className="glass-card wide-card" style={{ marginBottom: '2rem' }}>
            <SkeletonStats count={3} />
            <div className="sk-stack" style={{ marginTop: '1.5rem' }}>
              <Skel w="100%" h={10} />
              <Skel w="100%" h={10} />
            </div>
          </div>
          <div className="sk-stack" style={{ marginBottom: '1.5rem' }}>
            <Skel w={180} h={22} />
            <Skel w="72%" h={12} />
          </div>
          <PlanCardsSkeleton />
          <div className="glass-card wide-card">
            <Skel w={210} h={22} />
            <SkeletonTable rows={4} cols={4} />
          </div>
        </SkeletonRegion>
      </div>
    );
  }

  if (canManageBilling && !hasStripeSub && !success) {
    return <Navigate to="/assinar" replace />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Pagamentos e Planos</h1>
          <p className="subtitle">Assinaturas e créditos avulsos em USD</p>
        </div>
      </div>

      {success && (
        <div className="alert-banner alert-success" style={{ marginBottom: '1.5rem' }}>
          <Check size={18} />
          <span>Pagamento iniciado com sucesso. Os créditos aparecem após confirmação do Stripe (webhook).</span>
        </div>
      )}
      {canceled && (
        <div className="alert-banner alert-warning" style={{ marginBottom: '1.5rem' }}>
          <span>Checkout cancelado. Nenhuma cobrança foi feita.</span>
        </div>
      )}
      {isCanceling && (
        <div className="alert-banner alert-warning" style={{ marginBottom: '1.5rem' }}>
          <span>Assinatura agendada para cancelar ao fim do período atual.</span>
        </div>
      )}

      <div className="glass-card wide-card" style={{ marginBottom: '2rem' }}>
        <div className="stats-grid" style={{ marginBottom: 0 }}>
          <div className="stat-card stat-primary">
            <div className="stat-icon"><Coins size={24} /></div>
            <div className="stat-info">
              <span className="stat-label">Saldo Atual</span>
              <span className="stat-value">{fmtCredits(org?.available_credits ?? 0)}</span>
            </div>
          </div>
          <div className="stat-card stat-green">
            <div className="stat-icon"><CreditCard size={24} /></div>
            <div className="stat-info">
              <span className="stat-label">Plano Atual</span>
              <span className="stat-value" style={{ fontSize: '1.5rem', textTransform: 'capitalize' }}>
                {hasStripeSub ? (org?.plans?.name || '—') : 'Sem assinatura'}
              </span>
            </div>
          </div>
          {org?.plans?.can_invite_users && (
            <div className="stat-card">
              <div className="stat-icon"><Users size={24} /></div>
              <div className="stat-info">
                <span className="stat-label">Assentos extras</span>
                <span className="stat-value">{currentExtraSeats}</span>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: '1rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Créditos mensais</span>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                {fmtCredits(monthlyCredits)}
                {monthlyAllotment > 0 ? ` / ${fmtCredits(monthlyAllotment)}` : ''}
                <span style={{ opacity: 0.75 }}> · reinicia no ciclo</span>
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={monthlyCredits}
              aria-valuemin={0}
              aria-valuemax={monthlyBarMax}
              aria-label="Créditos mensais restantes"
              style={{
                height: 10,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.08)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${monthlyPct}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                  transition: 'width 0.35s ease',
                }}
              />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, gap: '1rem' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Créditos avulsos</span>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                {avulsos
                  ? `${fmtCredits(avulsos.spent)} de ${fmtCredits(avulsos.total)}`
                  : fmtCredits(purchasedCredits)}
                <span style={{ opacity: 0.75 }}> · acumulam, sem expirar</span>
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={avulsos?.spent ?? 0}
              aria-valuemin={0}
              aria-valuemax={purchasedBarMax}
              aria-label="Créditos avulsos gastos"
              style={{
                height: 10,
                borderRadius: 999,
                background: 'rgba(255,255,255,0.08)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${purchasedPct}%`,
                  height: '100%',
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, #10b981, #34d399)',
                  transition: 'width 0.35s ease',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {isEmpresarialCurrent && canManageBilling && (
        <div className="glass-card wide-card" style={{ marginBottom: '2rem' }}>
          <div className="section-header">
            <h2>Adicionar assentos</h2>
            <p className="subtitle">
              +{usd(19)} / mês e +500 créditos por assento. Confirmação no Stripe Checkout.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                Quantidade
              </label>
              <input
                type="number"
                min={1}
                max={50}
                className="form-input"
                style={{ width: '120px' }}
                value={addSeatsQty}
                onChange={(e) => setAddSeatsQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
              />
            </div>
            <div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Total no Checkout</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{usd(19 * addSeatsQty)}</div>
            </div>
            <button
              className="btn-primary"
              disabled={addingSeats || addSeatsQty < 1 || isCanceling}
              onClick={handleAddSeats}
            >
              {addingSeats ? (
                <div className="loading-spinner" style={{ width: 16, height: 16, borderTopColor: 'currentColor' }} />
              ) : (
                <><Users size={16} /> Comprar assentos</>
              )}
            </button>
          </div>

          {currentExtraSeats > 0 && (
            <>
              <hr style={{ margin: '1.75rem 0', border: 0, borderTop: '1px solid var(--border-color)' }} />
              <div className="section-header">
                <h2>Remover assentos vagos</h2>
                <p className="subtitle">
                  Só assentos sem membro. Ocupados: {memberCount}/{seatCap}
                  {removableVacantSeats > 0
                    ? ` · ${removableVacantSeats} vago(s) removível(is)`
                    : ' · nenhum vago'}
                </p>
              </div>
              {removableVacantSeats > 0 ? (
                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
                      Quantidade (máx. {removableVacantSeats})
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={removableVacantSeats}
                      className="form-input"
                      style={{ width: '120px' }}
                      value={removeSeatsQty}
                      onChange={(e) =>
                        setRemoveSeatsQty(
                          Math.max(
                            1,
                            Math.min(removableVacantSeats, parseInt(e.target.value, 10) || 1),
                          ),
                        )
                      }
                    />
                  </div>
                  <button
                    className="btn-secondary"
                    disabled={previewLoading || removingSeats || removeSeatsQty < 1 || !hasStripeSub}
                    onClick={openRemoveSeatsModal}
                  >
                    {previewLoading ? (
                      <div className="loading-spinner" style={{ width: 16, height: 16, borderTopColor: 'currentColor' }} />
                    ) : (
                      'Remover assentos vagos'
                    )}
                  </button>
                </div>
              ) : (
                <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                  Todos os assentos estão ocupados. Remova um membro em Gestão de usuários para liberar vagas.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {canManageBilling && hasStripeSub && !isCanceling && (
        <div className="glass-card wide-card" style={{ marginBottom: '2rem' }}>
          <div className="section-header">
            <h2>Cancelar assinatura</h2>
            <p className="subtitle">
              Agenda o cancelamento para o fim do ciclo. Créditos já creditados permanecem na conta.
            </p>
          </div>
          <button className="btn-secondary" disabled={canceling} onClick={handleCancelSubscription}>
            {canceling ? (
              <div className="loading-spinner" style={{ width: 16, height: 16, borderTopColor: 'currentColor' }} />
            ) : (
              'Cancelar ao fim do período'
            )}
          </button>
        </div>
      )}

      <h2>Planos Disponíveis</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
        1 crédito ≈ US$0.01 de orçamento de API. Assinaturas mensais em USD via Stripe Checkout.
      </p>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.88rem', lineHeight: 1.5 }}>
        Vídeos e imagens no modelo mais barato: {CHEAPEST_VIDEO.label} {CHEAPEST_VIDEO.resolution} ({CHEAPEST_VIDEO.seconds}s = {CHEAPEST_VIDEO.credits} créditos) ou {CHEAPEST_IMAGE.label} ({CHEAPEST_IMAGE.credits} créditos). Os agentes de IA (Estúdio, Tela em branco e Editor) debitam só o turno, por tokens — em geral uma fração de crédito — e a geração sai do mesmo saldo.
      </p>

      {loading ? (
        <SkeletonRegion label="Carregando planos…">
          <PlanCardsSkeleton />
        </SkeletonRegion>
      ) : (
        <div className="plans-grid">
          {plans.map((pkg) => {
            const isCurrentPlan = hasStripeSub && org?.plans?.id === pkg.id;
            const isEmpresarial = pkg.name === 'Empresarial';

            return (
              <div key={pkg.id} className={`plan-card ${isCurrentPlan ? 'selected' : ''}`} style={{ position: 'relative' }}>
                {isCurrentPlan && <div className="popular-badge">Seu Plano</div>}
                <div className="plan-name">{pkg.name}</div>
                <div className="plan-price">
                  {usd(Number(pkg.monthly_price))} <span>/mês</span>
                </div>
                <ul className="plan-features">
                  <li>
                    <span className="plan-credit-line">
                      {pkg.credits_per_month.toLocaleString('pt-BR')} créditos/mês
                      <button
                        type="button"
                        className="plan-info"
                        aria-label={planMediaLine(pkg.credits_per_month)}
                      >
                        <Info size={14} aria-hidden />
                        <span className="plan-info-tip" role="tooltip" aria-hidden="true">{planMediaLine(pkg.credits_per_month)}</span>
                      </button>
                    </span>
                  </li>
                  {planProductFeatures(readyModels).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                  {pkg.can_invite_users && <li>Equipe + assentos extras</li>}
                </ul>
                {isEmpresarial && !isCurrentPlan && (
                  <div className="form-group" style={{ marginTop: '0.75rem', width: '100%' }}>
                    <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      Assentos extras (+{usd(19)} / +500 créd. cada)
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      className="form-input"
                      value={extraSeats}
                      onChange={(e) => setExtraSeats(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    />
                  </div>
                )}
                <button
                  className={isCurrentPlan ? 'btn-secondary' : 'btn-primary'}
                  disabled={isCurrentPlan || subscribing === pkg.id}
                  style={{ marginTop: 'auto' }}
                  onClick={() => !isCurrentPlan && handleSubscribe(pkg.id, pkg.name)}
                >
                  {isCurrentPlan ? (
                    <><Check size={16} /> Ativo</>
                  ) : subscribing === pkg.id ? (
                    <div className="loading-spinner" style={{ width: 16, height: 16, borderTopColor: 'currentColor' }} />
                  ) : (
                    'Assinar'
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {hasStripeSub && (
      <div className="glass-card wide-card" style={{ marginTop: '2rem' }}>
        <div className="section-header">
          <h2>Créditos Avulsos</h2>
          <p className="subtitle">Pacotes em USD sem mudar de plano (US$0.01 / crédito)</p>
        </div>

        <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '250px' }}>
            <label htmlFor="pack-qty" style={{ display: 'block', marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>
              Quantidade em segundos ({CHEAPEST_VIDEO.creditsPerSecond} créditos/segundo · mín. {minPackSeconds})
            </label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <input
                id="pack-qty"
                type="number"
                min={minPackSeconds}
                step="1"
                value={seconds}
                onChange={(e) => setPackSeconds(Math.max(minPackSeconds, parseInt(e.target.value, 10) || minPackSeconds))}
                className="form-input"
                style={{ fontSize: '1.2rem', padding: '0.75rem', width: '120px' }}
              />
              <span style={{ fontSize: '1.2rem', fontWeight: 600 }}>segundos</span>
            </div>
            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              {CHEAPEST_VIDEO.label} {CHEAPEST_VIDEO.resolution} · {fmtCredits(customCredits)} créditos avulsos na conta
            </p>
          </div>

          <div style={{ padding: '1.5rem', background: 'var(--surface-color)', borderRadius: '12px', border: '1px solid var(--border-color)', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Total a pagar</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-color)' }}>
                {usd(customCredits * 0.01)}
              </div>
            </div>
            <button
              className="btn-primary"
              style={{ width: '100%' }}
              onClick={handleBuyCredits}
              disabled={buying || seconds < minPackSeconds}
            >
              {buying ? (
                <div className="loading-spinner" style={{ width: 16, height: 16, borderTopColor: 'currentColor' }} />
              ) : (
                'Comprar Avulso'
              )}
            </button>
          </div>
        </div>
      </div>
      )}

      <div className="glass-card wide-card" style={{ marginTop: '2rem' }}>
        <div className="section-header">
          <h2>Histórico de Compras</h2>
          <Link to="/financeiro" className="btn-link">Ver detalhes <ArrowRight size={16} /></Link>
        </div>
        {purchases === null ? (
          <SkeletonRegion label="Carregando compras…">
            <SkeletonTable rows={4} cols={4} />
          </SkeletonRegion>
        ) : purchases.length === 0 ? (
          <div className="empty-state">
            <CreditCard size={40} />
            <p>Nenhuma compra registrada ainda.</p>
          </div>
        ) : (
          <div className="data-table-wrapper" tabIndex={0} role="region" aria-label="Histórico de compras">
            <table className="data-table">
              <caption className="sr-only">Compras de créditos e créditos do plano</caption>
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Descrição</th>
                  <th scope="col">Créditos</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((row) => (
                  <tr key={row.id}>
                    <td className="text-muted">{row.created_at ? new Date(row.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                    <td>{PURCHASE_LABEL[row.type] || row.type}</td>
                    <td>{row.description || '—'}</td>
                    <td className={row.amount < 0 ? 'text-error' : 'text-success'}>
                      {row.amount > 0 ? '+' : ''}{fmtCredits(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {removePreview && (
        <ModalOverlay
          onClose={() => !removingSeats && setRemovePreview(null)}
          closeOnBackdrop={!removingSeats}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2>Confirmar remoção de assentos</h2>
              <button
                type="button"
                className="modal-close"
                disabled={removingSeats}
                onClick={() => setRemovePreview(null)}
              >
                <X size={20} />
              </button>
            </div>

            <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
              Remover <strong>{removePreview.remove_seats}</strong> assento(s) vago(s)
              ({removePreview.extra_seats_before} → {removePreview.extra_seats_after} extras).
              Equipe: {removePreview.members}/{removePreview.seat_cap}.
            </p>

            <div
              style={{
                display: 'grid',
                gap: '0.75rem',
                padding: '1rem',
                borderRadius: 12,
                border: '1px solid var(--border-color)',
                background: 'var(--surface-color)',
                marginBottom: '1rem',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Reembolso Stripe (este mês)</span>
                <strong>{usd((removePreview.credit_cents || 0) / 100)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Assentos / mês (antes)</span>
                <span>{usd(removePreview.monthly_seats_before_cents / 100)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Assentos / mês (próximo ciclo)</span>
                <span>{usd(removePreview.monthly_seats_after_cents / 100)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Economia a partir do próximo ciclo</span>
                <strong>{usd(Math.abs(removePreview.monthly_delta_cents) / 100)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Dias restantes no ciclo</span>
                <span>{removePreview.remaining_days}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Estorno de créditos da plataforma</span>
                <strong>0</strong>
              </div>
            </div>

            {removePreview.lines && removePreview.lines.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                  Detalhe Stripe (proração)
                </div>
                <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  {removePreview.lines.map((line, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      {line.description}:{' '}
                      <span style={{ color: line.amount_cents < 0 ? '#22c55e' : undefined }}>
                        {usd(line.amount_cents / 100)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {removePreview.credit_note}
            </p>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {removePreview.credits_note}
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button
                type="button"
                className="btn-secondary"
                disabled={removingSeats}
                onClick={() => setRemovePreview(null)}
              >
                Voltar
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={removingSeats}
                onClick={handleConfirmRemoveSeats}
              >
                {removingSeats ? (
                  <div className="loading-spinner" style={{ width: 16, height: 16, borderTopColor: 'currentColor' }} />
                ) : (
                  'Confirmar remoção'
                )}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
